import { differenceInDays } from 'date-fns';
import { useMemo } from 'react';
import { SummaryStatTile } from '../../../patients/views/details/SummaryStatistics';
import { formatSecondsString } from '../../../util/date';
import { ClinicianDashboardInsights } from '../hooks/useClinicianDashboardData';

export interface ClinicianInsightsProps {
  dateRange?: [Date, Date] | null;
  insights?: ClinicianDashboardInsights;
  isLoading?: boolean;
}

export function ClinicianInsights({ dateRange, insights, isLoading }: ClinicianInsightsProps) {
  return (
    <div className="flex flex-col gap-5 md:flex-row">
      <SummaryStatTile
        title="Total sessions"
        value={insights?.totalSessions.value?.toFixed() ?? ''}
        prevValue={insights?.totalSessions.prevValue?.toFixed() ?? ''}
        trend={insights?.totalSessions.trend}
        isLoading={isLoading}
      />
      <AvgSessionsStatTile dateRange={dateRange} insights={insights} isLoading={isLoading} />
      <SummaryStatTile
        title="Avg reps per session"
        value={insights?.avgRepsPerSession.value?.toFixed() ?? ''}
        prevValue={insights?.avgRepsPerSession.prevValue?.toFixed() ?? ''}
        trend={insights?.avgRepsPerSession.trend}
        isLoading={isLoading}
      />
      <SummaryStatTile
        title="Avg time on task per session"
        value={formatSecondsString(insights?.avgTimeTakenPerSession?.value ?? 0)}
        prevValue={formatSecondsString(insights?.avgTimeTakenPerSession?.prevValue ?? 0)}
        trend={insights?.avgTimeTakenPerSession.trend}
        isLoading={isLoading}
      />
    </div>
  );
}

function AvgSessionsStatTile({ dateRange, insights, isLoading }: ClinicianInsightsProps) {
  const { insight, title } = useMemo(() => {
    const daysDiff = dateRange ? differenceInDays(dateRange[1], dateRange[0]) : Infinity;

    let insight = insights?.avgSessionsPerMonth;
    let title = 'Avg sessions per month';
    if (daysDiff < 14) {
      insight = insights?.avgSessionsPerDay;
      title = 'Avg sessions per day';
    } else if (daysDiff < 62) {
      insight = insights?.avgSessionsPerWeek;
      title = 'Avg sessions per week';
    }

    return { insight, title };
  }, [dateRange, insights]);

  return (
    <SummaryStatTile
      title={title}
      value={insight?.value?.toFixed() ?? ''}
      prevValue={insight?.prevValue?.toFixed() ?? ''}
      trend={insight?.trend}
      isLoading={isLoading}
    />
  );
}
